import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import parse from 'html-react-parser';
import './search.css';
import { getUserBookMarks } from '../utilities/user-data/UserData';
import { useSelector } from 'react-redux';
import { auth, db } from '../config/firebase';

import {
  getPageContext,
  initiate_interval_tree,
} from '../util/interval_tree.ts';
import { bookHistory, key_bookUrl } from '../util/constants';

/**
 * Checks if a string is highly likely to be English.
 * @param {string} text - The input string to check.
 * @returns {boolean} - True if the string is likely English, otherwise false.
 */
function isLikelyEnglish(text) {
  if (!text || typeof text !== 'string') {
    return false; // Empty or non-string inputs are not English
  }

  // Define a regex to match common English characters (letters, numbers, and basic punctuation)
  const englishCharRegex = /^[a-zA-Z0-9 .,?!'"\-\(\)\[\]\/:;]+$/;

  // Count the number of matching English characters
  let englishCharCount = 0;
  let totalCharCount = text.length;

  for (let char of text) {
    if (char.match(englishCharRegex)) {
      englishCharCount++;
    }
  }

  // Determine the threshold (e.g., 80% English characters for "likely English")
  const englishCharRatio = englishCharCount / totalCharCount;

  return englishCharRatio > 0.8; // Adjust threshold as needed
}

// A custom modal to replace alert()
const CustomAlert = ({ message, onClose }) => {
  if (!message) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full text-center">
        <p className="text-gray-800 mb-4">{message}</p>
        <button
          onClick={onClose}
          className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default function Search(props) {
  const [data, setData] = useState([]);
  const [userBookMarks, setUserBookMarks] = useState([]);
  const [userBookMarkDict, setUserBookMarkDict] = useState({});
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.userReducer);
  const [isLoading, setIsLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState('');

  // State for filtering and pagination
  const [filter, setFilter] = useState('all'); // 'all', 'bookmarked'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Create a ref for the top of the content area
  const topOfPageRef = useRef(null);

  initiate_interval_tree();

  useEffect(() => {
    const search = async () => {
      setIsLoading(true);
      let searchText = searchParams.get('q') || 'philosophy'; // Default search for demo
      let searchType = searchParams.get('searchType') || 'simple';
      let url = '';

      try {
        if (!searchText) {
          setIsLoading(false);
          return;
        }

        if (searchText.length > 128) {
          setAlertMessage(
            'Search query is too long. Please try a shorter one.'
          );
          setIsLoading(false);
          return;
        }

        if (!isLikelyEnglish(searchText)) {
          // Simulate translation API call
          console.log(`Translating non-English text: ${searchText}`);
          const response = await fetch(
            `https://asia-south1-woven-benefit-320216.cloudfunctions.net/mareng?message=${searchText}`
          );
          if (!response.ok) {
            setAlertMessage(
              'We had trouble translating your search text. Please try again.'
            );
            throw new Error('Network response was not ok');
          }
          const json = await response.json();
          searchText = json.translatedText;
          searchType = 'vector';
          console.log(`Translated text: ${searchText}`);
        }

        if (searchType === 'simple') {
          url = `https://lqpsx6t02d.execute-api.ap-south-1.amazonaws.com/default/baws-search?q=${searchText}`;
        } else if (searchType === 'vector') {
          url = `https://0pe3xvyfpi.execute-api.ap-south-1.amazonaws.com/prod/search?page=1&size=25&vector_search=true&q=${searchText}`;
        } else {
          url = `https://0pe3xvyfpi.execute-api.ap-south-1.amazonaws.com/prod/search?page=1&size=25&filter_stop_words=true&q=${searchText}`;
        }

        const [searchResponse, bookmarks] = await Promise.all([
          fetch(url),
          user.isLoggedIn && auth?.currentUser?.uid
            ? getUserBookMarks(auth.currentUser.uid)
            : Promise.resolve([]),
        ]);

        if (bookmarks.length > 0) {
          const bookmarkDict = bookmarks.reduce((acc, item) => {
            acc[item.url] = item;
            return acc;
          }, {});
          setUserBookMarkDict(bookmarkDict);
        }

        if (!searchResponse.ok) {
          setAlertMessage('Results not found. Please try another search term.');
          throw new Error('Network response was not ok');
        }

        const json = await searchResponse.json();

        if (json && json.results.length > 0) {
          setCurrentPage(1); // Reset to first page on new search
          setData(json.results);
        } else {
          setData([]);
          setAlertMessage('No results found for your query.');
        }
      } catch (error) {
        console.error('Search failed:', error);
        setData([]);
        if (!alertMessage) {
          setAlertMessage(
            'An unexpected error occurred. Please try again later.'
          );
        }
      } finally {
        setIsLoading(false);
      }
    };

    search();
  }, [searchParams, user.isLoggedIn]); // Scroll to top when currentPage changes

  useEffect(() => {
    if (topOfPageRef.current) {
      topOfPageRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentPage]);

  const setBookContextUrl = (e, volName, pageNum) => {
    e.preventDefault();

    bookHistory.set(key_bookUrl, {
      shortUrl: e.target.href,
      longUrl: `https://assets.studyring.org/assets/books/baws/EN/${volName}.pdf#page=${pageNum}`,
    });

    navigate(`/books/baws/EN/${volName}/pdf/${pageNum}`);
  };

  // Memoized filtering and pagination
  const filteredData = useMemo(() => {
    if (filter === 'all') {
      return data;
    }
    return data.filter((item) => {
      const resultUrl = `baws.in/books${item.path.raw}${item.container.raw}/pdf/${item.actual_page.raw}`;
      return userBookMarkDict.hasOwnProperty(resultUrl);
    });
  }, [data, filter, userBookMarkDict]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="bg-blue-50 font-sans min-h-screen">
      <CustomAlert message={alertMessage} onClose={() => setAlertMessage('')} />
      <div
        className="container mx-auto px-4 pt-4 pb-8 sm:px-6 lg:px-8"
        ref={topOfPageRef} // Attach the ref here
        style={{ scrollMarginTop: '64px' }}
      >
        {/* Filter Controls - Hidden for now
        <div className="flex justify-end mb-6">
          <div className="relative">
            <select
              onChange={(e) => {
                setFilter(e.target.value);
                setCurrentPage(1); // Reset to first page when filter changes
              }}
              value={filter}
              className="appearance-none w-full bg-white border border-blue-300 text-blue-800 py-2 pl-3 pr-8 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Show All</option>
              <option value="bookmarked">Bookmarked Only</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-blue-700">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
            </div>
          </div>
        </div>
        */}

        {/* Loading Spinner */}
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-t-4 border-b-4 border-blue-500"></div>
          </div>
        ) : (
          <>
            {/* Results Grid */}
            <div className="grid gap-6">
              {paginatedData.length > 0 ? (
                paginatedData.map((searchRow) => {
                  const pageContext = getPageContext(searchRow);
                  let title = searchRow.data.container;
                  if (pageContext.length > 0) {
                    try {
                      const item = JSON.parse(
                        pageContext[pageContext.length - 1]
                      );
                      title = item.label;
                    } catch (e) {
                      console.error('Failed to parse page context', e);
                    }
                  }

                  const resultUrl = `baws.in/books${searchRow.path.raw}${searchRow.container.raw}/pdf/${searchRow.actual_page.raw}`;
                  const isBookmarked =
                    userBookMarkDict.hasOwnProperty(resultUrl);

                  return (
                    <div
                      key={searchRow.id.raw}
                      className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300 ease-in-out transform hover:-translate-y-1 overflow-hidden border border-blue-100"
                    >
                      <div className="p-6">
                        <h2 className="text-xl font-bold text-blue-800 mb-3 flex justify-between items-start">
                          <a
                            href={`/books${searchRow.path.raw}${searchRow.container.raw}/pdf/${searchRow.actual_page.raw}`}
                            onClick={(e) =>
                              setBookContextUrl(
                                e,
                                searchRow.container.raw,
                                searchRow.actual_page.raw
                              )
                            }
                            className="hover:text-blue-600 transition-colors"
                          >
                            {title}
                          </a>
                          {isBookmarked && (
                            <span className="text-xl" title="Bookmarked">
                              ⭐
                            </span>
                          )}
                        </h2>

                        <div className="prose prose-blue max-w-none text-gray-700 mb-4">
                          {parse(
                            searchRow.data.snippet.replace(/em>/g, 'strong>')
                          )}
                        </div>

                        <div className="text-sm text-blue-600 hover:underline mb-4">
                          <a
                            href={`/books${searchRow.path.raw}${searchRow.container.raw}/pdf/${searchRow.actual_page.raw}`}
                            onClick={(e) =>
                              setBookContextUrl(
                                e,
                                searchRow.container.raw,
                                searchRow.actual_page.raw
                              )
                            }
                          >
                            {`${searchRow.container.raw} (Page No: ${searchRow.actual_page.raw})`}
                          </a>
                        </div>

                        {/* Breadcrumbs */}
                        <div className="flex flex-wrap items-center text-xs text-gray-500 border-t border-blue-100 pt-3">
                          {pageContext.map((context_str, i) => {
                            try {
                              const { vol_name, page_start, label } =
                                JSON.parse(context_str);
                              return (
                                <React.Fragment
                                  key={`${searchRow.id.raw}-${i}`}
                                >
                                  <a
                                    href={`/books/baws/EN/${vol_name}/pdf/${page_start}`}
                                    onClick={(e) =>
                                      setBookContextUrl(e, vol_name, page_start)
                                    }
                                    className="text-blue-500 hover:text-blue-700 transition-colors"
                                  >
                                    {label}
                                  </a>
                                  {i < pageContext.length - 1 && (
                                    <span className="mx-2 text-blue-300">
                                      /
                                    </span>
                                  )}
                                </React.Fragment>
                              );
                            } catch (e) {
                              return null;
                            }
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-16 bg-white rounded-lg shadow-md">
                  <h2 className="text-2xl font-semibold text-gray-700">
                    No Results to Display
                  </h2>
                  <p className="text-gray-500 mt-2">
                    Try adjusting your filters or search for something else.
                  </p>
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center mt-10 space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-white text-blue-700 font-semibold border border-blue-200 rounded-lg shadow-sm hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-blue-800 font-medium">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-white text-blue-700 font-semibold border border-blue-200 rounded-lg shadow-sm hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
